import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const CONTENT = {
    privacy: {
        label: "Legal",
        title: "Privacy",
        highlight: "Policy",
        description: "How DtOrbit handles the information you share with us through this website.",
        metaTitle: "Privacy Policy | DtOrbit",
        metaDesc: "Read the DtOrbit privacy policy — what information we collect through this website and how it is used.",
        path: "/privacy",
        sections: [
            {
                h: "Information We Collect",
                p: "When you use the contact form, careers application form or newsletter signup on this website, we collect the details you provide — such as your name, email address, phone number, message and (for job applications) your resume. The USDT to INR exchange itself takes place on the official DtOrbit platform at dtorbit.com.",
            },
            {
                h: "How We Use Information",
                p: "The information you submit is used only to respond to your enquiry, process your job application or send you the updates you subscribed to. We do not sell your personal information to third parties.",
            },
            {
                h: "Data Retention & Security",
                p: "Submitted information is stored securely and retained only as long as needed for the purpose it was collected. Access is limited to the DtOrbit team members who need it.",
            },
            {
                h: "Changes & Contact",
                p: "This policy may be updated as the website evolves. If you have questions about your data, reach out through the contact form on this website.",
            },
        ],
    },
    terms: {
        label: "Legal",
        title: "Terms of",
        highlight: "Use",
        description: "The terms that apply when you use the DtOrbit marketing website.",
        metaTitle: "Terms of Use | DtOrbit",
        metaDesc: "Read the terms of use for the DtOrbit website — the official informational site for the DtOrbit USDT to INR exchange platform.",
        path: "/terms",
        sections: [
            {
                h: "Purpose of This Website",
                p: "This website provides information about DtOrbit and its USDT to INR exchange platform, including the live exchange rate. All exchange activity takes place on the official DtOrbit platform at dtorbit.com and is subject to that platform's own terms.",
            },
            {
                h: "No Financial Advice",
                p: "Content on this website is for general information only and does not constitute financial, investment or legal advice. Exchange rates are shown for informational purposes and may change at any time.",
            },
            {
                h: "Acceptable Use",
                p: "You agree not to misuse this website, attempt to disrupt its operation, or use its content in a misleading way. The DtOrbit name and logo are the property of DtOrbit.",
            },
            {
                h: "Changes",
                p: "DtOrbit may update these terms from time to time. Continued use of the website after changes are published means you accept the updated terms.",
            },
        ],
    },
    risk: {
        label: "Legal",
        title: "Risk",
        highlight: "Disclosure",
        description: "Important points to understand before exchanging digital assets.",
        metaTitle: "Risk Disclosure | DtOrbit",
        metaDesc: "Read the DtOrbit risk disclosure — important considerations before exchanging USDT or other digital assets for INR.",
        path: "/risk-disclosure",
        sections: [
            {
                h: "Exchange Rates Can Change",
                p: "The USDT to INR rate is live and can move at any time. The rate you see before starting an exchange may differ from the rate applied when your transaction is processed. Always review the rate shown at the time of your exchange.",
            },
            {
                h: "Digital Asset Risks",
                p: "Digital assets such as USDT involve risk, including network congestion, transaction errors and value fluctuations of the underlying market. Sending assets to an incorrect address or network can result in permanent loss.",
            },
            {
                h: "Verify Before You Send",
                p: "Always double-check wallet addresses, networks and payout details before confirming a transaction, and make sure you are using the official dtorbit.com website.",
            },
            {
                h: "Your Responsibility",
                p: "You are responsible for understanding the risks of digital asset transactions and for complying with the laws and tax rules that apply to you. If you are unsure, consider seeking independent professional advice.",
            },
        ],
    },
};

export default function Legal({ kind }) {
    const page = CONTENT[kind] || CONTENT.privacy;

    usePageMeta({
        title: page.metaTitle,
        description: page.metaDesc,
        path: page.path,
    });

    return (
        <>
            <PageHero
                testid={`legal-hero-${kind}`}
                label={page.label}
                title={page.title}
                highlight={page.highlight}
                description={page.description}
            />
            <section data-testid={`legal-content-${kind}`} className="relative py-14 md:py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 flex flex-col gap-6">
                    {page.sections.map((section, i) => (
                        <Reveal key={section.h} delay={i * 60}>
                            <div className="glass rounded-2xl p-6 md:p-7">
                                <h2 className="font-display text-lg font-bold text-[#0F1B3D]">
                                    {section.h}
                                </h2>
                                <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed">
                                    {section.p}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                    <p className="text-xs text-[#8494B0]">
                        Last updated: August 2026
                    </p>
                </div>
            </section>
        </>
    );
}
